import { memo, useMemo } from 'react'

import { ListItemIcon, Menu, MenuItem } from '@mui/material'

import { getContentId } from '@tabeeb/modules/shared/content/selectors'
import { setFormAnswerStatus } from '@tabeeb/modules/forms/actions'
import { makeGetAnswerStatusDropdownOptions, makeGetSelectedAnswerStatus } from '@tabeeb/modules/forms/selectors'

import { useDispatch, useSelector } from '@tabeeb/store'
import { ButtonProps, Tooltip, usePopoverState } from '@collaborate-health/uikit'
import { FormAnswerStatus } from '@tabeeb/contracts/main-api/swagger-main-api'

import { FormControlAnswer } from '../../types'

import AnswerStatusIcon, { AnswerStatusIconProps } from '../AnswerStatusIcon'

import { AnswerStatusButton, AnswerStatusText } from './AnswerStatus.styles'

export interface AnswerStatusProps {
  answer?: FormControlAnswer | null
  isOnReview: boolean
}

export const getAnswerStatusButtonColor = (status: FormAnswerStatus): ButtonProps['color'] => {
  switch (status) {
    case FormAnswerStatus.Pending:
      return 'info'
    case FormAnswerStatus.Pass:
    case FormAnswerStatus.ApprovedByAI:
    case FormAnswerStatus.ApprovedByReviewer:
      return 'success'
    case FormAnswerStatus.Fail:
    case FormAnswerStatus.RejectedByAI:
    case FormAnswerStatus.RejectedByReviewer:
      return 'error'
    default:
      return 'default'
  }
}

export const getAnswerStatusIconColor = (status: FormAnswerStatus): AnswerStatusIconProps['color'] => {
  switch (status) {
    case FormAnswerStatus.Pending:
      return 'info'
    case FormAnswerStatus.Pass:
    case FormAnswerStatus.ApprovedByAI:
    case FormAnswerStatus.ApprovedByReviewer:
      return 'success'
    case FormAnswerStatus.Fail:
    case FormAnswerStatus.RejectedByAI:
    case FormAnswerStatus.RejectedByReviewer:
      return 'error'
    default:
      return 'action'
  }
}
const AnswerStatus: React.FC<AnswerStatusProps> = ({ answer, isOnReview }) => {
  const dispatch = useDispatch()

  const contentId = useSelector(getContentId)
  const [anchorRef, open, onOpen, onClose] = usePopoverState<HTMLButtonElement>()

  const handleStatusChange = (value: number) => {
    dispatch(
      setFormAnswerStatus({
        FormAnswerStatus: value,
        ContentId: contentId,
        FormAnswerId: answer!.Id,
      })
    )
  }

  const statusSelector = useMemo(makeGetSelectedAnswerStatus, [])
  const status = useSelector((state) => statusSelector(state, { answer }))

  const optionsSelector = useMemo(makeGetAnswerStatusDropdownOptions, [])
  const options = useSelector((state) => optionsSelector(state, { answer }))
  const isDisabled = status.isDisabled || isOnReview

  return (
    <>
      <Tooltip placement='top' text={status.displayValue}>
        <span>
          <AnswerStatusButton
            ref={anchorRef}
            size='small'
            startIcon={<AnswerStatusIcon answerStatus={answer?.FormAnswerStatus ?? FormAnswerStatus.Empty} />}
            color={getAnswerStatusButtonColor(status.value)}
            disabled={isDisabled}
            onClick={onOpen}
          >
            <AnswerStatusText>{status.displayValue}</AnswerStatusText>
          </AnswerStatusButton>
        </span>
      </Tooltip>

      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {options.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => {
              handleStatusChange(option.value)
              onClose()
            }}
          >
            <ListItemIcon>
              <AnswerStatusIcon color={getAnswerStatusIconColor(option.value)} answerStatus={option.value} />
            </ListItemIcon>
            {option.displayValue}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default memo(AnswerStatus)

